import { useEffect, useRef, type RefObject } from "react";

/**
 * L-16: klik poza panelem i Escape zamykaja go; Escape zwraca fokus na wyzwalacz (A-4).
 *
 * Referencje podajemy JAWNIE i moze ich byc kilka, bo panel i jego wyzwalacz bywaja
 * rozlacznymi poddrzewami. Tak jest z hamburgerem: przycisk siedzi w headerze,
 * a kafelek nawigacji obok niego w DOM - klik w przycisk nie moze liczyc sie jako
 * "klik poza kafelkiem", inaczej menu zamykaloby sie w tej samej chwili, w ktorej
 * sie otwiera.
 *
 * Sluchamy `click`, nie `pointerdown`: przy pointerdown zamkniecie wyprzedziloby
 * klikniecie w pozycje panelu i akcja by nie doszla.
 *
 * Hook celowo NIE polega na stopPropagation w wyzwalaczach - to kruche, bo wymaga
 * pamietania o nim w kazdym nowym elemencie panelu. Zamiast tego sprawdzamy
 * przynaleznosc celu do ktoregokolwiek z podanych poddrzew.
 */
export function useOutsideDismiss(
  otwarte: boolean,
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  zamknij: (zwrocFokus: boolean) => void,
): void {
  // Tablica refow jest tworzona na nowo przy kazdym renderze wywolujacego, wiec jako
  // zaleznosc efektu przepinalaby sluchacze bez potrzeby. Trzymamy ja w refie:
  // efekt czyta zawsze aktualna zawartosc, a sam zalezy tylko od tego, co go realnie
  // dotyczy - czy panel jest otwarty i jak go zamknac.
  const aktualneRefs = useRef(refs);
  aktualneRefs.current = refs;

  useEffect(() => {
    if (!otwarte) return;

    const naKlik = (e: MouseEvent): void => {
      const cel = e.target as Node | null;
      if (!cel) return;
      if (aktualneRefs.current.some((r) => r.current?.contains(cel))) return;
      zamknij(false);
    };

    const naKlawisz = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      // A-4: po zamknieciu klawiatura fokus wraca na wyzwalacz, a nie leci na <body>.
      zamknij(true);
    };

    document.addEventListener("click", naKlik);
    document.addEventListener("keydown", naKlawisz);
    return () => {
      document.removeEventListener("click", naKlik);
      document.removeEventListener("keydown", naKlawisz);
    };
  }, [otwarte, zamknij]);
}
